import { Form, Formik, FormikErrors, setIn } from 'formik';

import { VesselCreateRequestMessage } from '../../../types/VesselCreateRequestMessage';
import { Button } from '../../cdl/Button/Button';
import { FormikCheckbox } from '../../cdl/Checkbox/FormikCheckbox';
import { DrawerContentLayout } from '../../cdl/Drawer/DrawerContentLayout';
import { FormikInput } from '../../cdl/Input/FormikInput';
import { Text } from '../../cdl/Text/Text';
import { FormikCustomerCompanySelect } from '../../common/form-elements/formik/FormikCustomerCompanySelect';
import { FormikDebug } from '../../common/form-elements/formik/FormikDebug';
import { isValidEmailAddress } from '../../common/helpers/isValidEmailAddress.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { Box } from '../../common/ui/Box';
import { FormikVesselDefaultSupplierFlow } from '../FormikVesselDefaultSupplierFlow';

interface CreateVesselFormProps {
    onSubmit: (vessel: VesselCreateRequestMessage) => Promise<void>;
}

interface FormikVesselDetailsState {
    customerId: string;
    name: string;
    imo: string;
    types: string[];
    services: string[];
    email: string;
    defaultSupplierId: string;
    defaultContractId: string | null;
    invoiceVAT: string;
    invoiceEmailAddress: string;
    invoiceAddress: {
        companyName: string;
        street: string;
        streetNumber: string;
        zipcode: string;
        city: string;
        country: string;
    };
    postalAddress: {
        companyName: string;
        street: string;
        streetNumber: string;
        zipcode: string;
        city: string;
        country: string;
    };
    active: boolean;
    paying: boolean;
    directOrderAllowed: boolean;
}

export const CreateVesselForm = ({ onSubmit }: CreateVesselFormProps) => {
    const role = useRole();
    const companyIds = role.getCompanies().map((it) => it.id);
    const customerId = companyIds.at(0);

    const initialValues: FormikVesselDetailsState = {
        customerId: customerId && role.isOneCompanyUser() ? customerId : '',
        name: '',
        imo: '',
        types: [],
        services: [],
        email: '',
        defaultSupplierId: '',
        defaultContractId: null,
        invoiceVAT: '',
        invoiceEmailAddress: '',
        invoiceAddress: {
            companyName: '',
            street: '',
            streetNumber: '',
            zipcode: '',
            city: '',
            country: '',
        },
        postalAddress: {
            companyName: '',
            street: '',
            streetNumber: '',
            zipcode: '',
            city: '',
            country: '',
        },
        active: false,
        paying: false,
        directOrderAllowed: true,
    };

    const validateCreateVesselForm = (values: FormikVesselDetailsState) => {
        let errors: FormikErrors<FormikVesselDetailsState> = {};
        if (!role.isOneCompanyUser() && !values.customerId) {
            errors.customerId = translate('assignments.suppliers.companyRequired');
        }
        if (!values.name) {
            errors.name = translate('vessel.form.nameRequired');
        }
        if (!values.imo) {
            errors.imo = translate('vessel.form.imoRequired');
        }
        if (values.email && !isValidEmailAddress(values.email)) {
            errors.email = translate('shareOrderPopup.emailAddressError');
        }
        if (!values.invoiceAddress.companyName) {
            errors = setIn(errors, 'invoiceAddress.companyName', translate('vessel.form.companyNameRequired'));
        }
        if (!values.invoiceAddress.street) {
            errors = setIn(errors, 'invoiceAddress.street', translate('vessel.form.streetRequired'));
        }
        if (!values.invoiceAddress.streetNumber) {
            errors = setIn(errors, 'invoiceAddress.streetNumber', translate('vessel.form.streetNumberRequired'));
        }
        if (!values.invoiceAddress.zipcode) {
            errors = setIn(errors, 'invoiceAddress.zipcode', translate('vessel.form.zipCodeRequired'));
        }
        if (!values.invoiceAddress.city) {
            errors = setIn(errors, 'invoiceAddress.city', translate('vessel.form.cityRequired'));
        }
        if (!values.invoiceAddress.country) {
            errors = setIn(errors, 'invoiceAddress.country', translate('vessel.form.countryRequired'));
        }
        return errors;
    };

    const handleSubmit = async (values: FormikVesselDetailsState) => {
        const vesselRequestMessage: VesselCreateRequestMessage = {
            customerId: values.customerId,
            name: values.name,
            imo: values.imo,
            types: values.types.length ? values.types : undefined,
            services: values.services.length ? values.services : undefined,
            email: values.email || undefined,
            defaultSupplierId: values.defaultSupplierId || undefined,
            defaultContractId: values.defaultContractId || undefined,
            invoiceVAT: values.invoiceVAT || undefined,
            invoiceEmailAddress: values.invoiceEmailAddress || undefined,
            invoiceAddress: values.invoiceAddress,
            postalAddress: values.postalAddress,
            active: values.active,
            paying: values.paying,
            directOrderAllowed: values.directOrderAllowed,
        };
        await onSubmit(vesselRequestMessage);
    };

    return (
        <Formik initialValues={initialValues} validate={validateCreateVesselForm} onSubmit={handleSubmit}>
            {({ isSubmitting, values, dirty }) => (
                <Form style={{ height: '100%', minHeight: 0 }}>
                    <DrawerContentLayout
                        isBusy={isSubmitting}
                        hasUnsavedChanges={dirty}
                        primaryAction={
                            <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                                {translate('createVesselForm.createVessel')}
                            </Button>
                        }
                    >
                        <Box display="flex" flexDirection="column" gap={32}>
                            <Box display="grid" gap={16}>
                                {!role.isOneCompanyUser() ? (
                                    <FormikCustomerCompanySelect
                                        label={translate('vessel.company')}
                                        name="customerId"
                                    />
                                ) : null}

                                <FormikInput name="name" label={translate('vessel.name')} />

                                <FormikInput name="imo" label={translate('vessel.imo')} />

                                <FormikInput name="email" label={translate('vessel.form.email')} markAsOptional />

                                <FormikVesselDefaultSupplierFlow customerId={values.customerId} />
                            </Box>

                            <Box display="grid" gap={16}>
                                <Text variant="subtitle1">{translate('vessel.form.invoiceInformation')}</Text>
                                <FormikInput name="invoiceVAT" label={translate('vessel.form.vat')} markAsOptional />
                                <FormikInput
                                    name="invoiceEmailAddress"
                                    label={translate('vessel.form.invoiceEmailAddress')}
                                    markAsOptional
                                />
                                <FormikInput
                                    name="invoiceAddress.companyName"
                                    label={translate('global.address.company')}
                                />

                                <Box display="flex" flexDirection="column" gap={8}>
                                    <FormikInput
                                        name="invoiceAddress.street"
                                        label={translate('vessel.form.billingAddress')}
                                        placeholder={translate('vessel.form.streetName')}
                                    />
                                    <FormikInput
                                        name="invoiceAddress.streetNumber"
                                        placeholder={translate('vessel.form.streetNumber')}
                                    />
                                    <FormikInput
                                        name="invoiceAddress.zipcode"
                                        placeholder={translate('vessel.form.postalCode')}
                                    />
                                    <FormikInput
                                        name="invoiceAddress.city"
                                        placeholder={translate('vessel.form.townCity')}
                                    />
                                    <FormikInput
                                        name="invoiceAddress.country"
                                        placeholder={translate('global.address.country')}
                                    />
                                </Box>
                            </Box>

                            <Box display="grid" gap={16}>
                                <Text variant="subtitle1">{translate('vessel.form.postalAddress')}</Text>
                                <FormikInput
                                    name="postalAddress.companyName"
                                    label={translate('global.address.company.optional')}
                                    markAsOptional
                                />

                                <Box display="flex" flexDirection="column" gap={8}>
                                    <FormikInput
                                        name="postalAddress.street"
                                        label={translate('vessel.form.address.optional')}
                                        placeholder={translate('vessel.form.streetName')}
                                        markAsOptional
                                    />
                                    <FormikInput
                                        name="postalAddress.streetNumber"
                                        placeholder={translate('vessel.form.streetNumber')}
                                    />
                                    <FormikInput
                                        name="postalAddress.zipcode"
                                        placeholder={translate('vessel.form.postalCode')}
                                    />
                                    <FormikInput
                                        name="postalAddress.city"
                                        placeholder={translate('vessel.form.townCity')}
                                    />
                                    <FormikInput
                                        name="postalAddress.country"
                                        placeholder={translate('global.address.country')}
                                    />
                                </Box>
                            </Box>

                            {role.isSuperAdmin() ? (
                                <Box display="grid" rowGap={8} marginTop={8}>
                                    <Text variant="subtitle1" as="h2">
                                        {translate('vessel.form.adminSettings')}
                                    </Text>
                                    <FormikCheckbox name="active" label={translate('vessel.form.activeVessel')} />
                                    <FormikCheckbox name="paying" label={translate('vessel.form.payingVessel')} />
                                    <FormikCheckbox
                                        name="directOrderAllowed"
                                        label={translate('vessel.form.directOrder')}
                                    />
                                </Box>
                            ) : null}

                            <FormikDebug />
                        </Box>
                    </DrawerContentLayout>
                </Form>
            )}
        </Formik>
    );
};
